import { FlexCol, FlexRow } from '@/components/common/Flex';
import {
    SHUTDOWN_BANNER_HEIGHT,
    useIsShutdownBannerVisible,
} from '@/components/common/pageLayout/ShutdownBanner';
import ProductboardButton from '@/components/feedback/ProductboardButton';
import IDVStatusButton from '@/components/idv/IDVStatusButton';
import PartnerInviteButton from '@/components/partnershipInvites/PartnerInviteButton';
import {
    buildSettingsNavItems,
    isSettingsPath,
} from '@/components/settings/SettingsSidebar';
import { useExportActionText } from '@common/react/export/useExportActionText';
import { PAGE_MODE_MINE_COLOR, usePageContext } from '@common/react/pageMode';
import {
    CurrentUserContext,
    getPreferredOrFirstName,
    isSingleplayer,
    useCurrentUser,
} from '@common/react/user';
import { useShowReferrals } from '@common/react/useShowReferrals';
import styleguide from '@common/styleguide';
import NextLink from 'next/link';
import { usePathname } from 'next/navigation';
import { Button, IconButton } from '../Button';
import { PlentyLogo, Settings02 } from '../icons';
import SidebarNav, { SidebarNavPageItem } from '../SidebarNav';
import { hoverStyleProps } from '../styleHelpers';
import { Text } from '../Typography';
import UserAvatar from '../UserAvatar';
import PageModeToggle from './PageModeToggle';
import PrivacyModeTag from './PrivacyModeTag';

const buildMainNavItems = ({
    pathname,
    currentUser,
    exportActionText,
}: {
    currentUser: CurrentUserContext;
    pathname: string;
    exportActionText: string;
}): SidebarNavPageItem[] => {
    const items: SidebarNavPageItem[] = [
        {
            href: currentUser.featureFlags.isShutdownV1Enabled
                ? '/'
                : '/export',
            text: exportActionText,
            isHidden: !currentUser.featureFlags.isShutdownV1Enabled,
            isSelected: currentUser.featureFlags.isShutdownV1Enabled
                ? '/' === pathname || '/export' === pathname
                : '/export' === pathname,
        },
        {
            href: currentUser.featureFlags.isShutdownV1Enabled
                ? '/dashboard'
                : '/',
            text: 'Dashboard',
            isSelected: currentUser.featureFlags.isShutdownV1Enabled
                ? '/dashboard' === pathname
                : '/' === pathname || '/dashboard' === pathname,
            indicatorColor: '#5DBC3C',
        },
        {
            href: '/investing',
            text: 'Investments',
            isSelected: pathname === '/investing',
            indicatorColor: '#4EBDF8',
            isHidden:
                currentUser.userInfo.atomicStatus !== 'ACCOUNT_COMPLETE' &&
                currentUser.userInfo.atomicStatus !==
                    'VERIFICATION_SUCCESSFUL' &&
                !currentUser.userInfo.isAdmin,
        },
        {
            href: '/budget',
            text: 'Budget',
            isSelected: pathname === '/budget',
            indicatorColor: styleguide.colors.indigo,
        },
        {
            href: '/cashflow',
            text: 'Cashflow',
            isSelected: pathname.includes('/cashflow'),
            indicatorColor: '#FF8484',
        },
        {
            href: '/transactions',
            text: 'Transactions',
            isSelected: '/transactions' === pathname,
            indicatorColor: '#FFE285',
        },
    ];

    return items.filter((i) => !i.isHidden);
};

export const SIDEBAR_WIDTH = 220;

const LeftNavSidebar = () => {
    const pathname = usePathname();
    const exportActionText = useExportActionText();
    const currentUser = useCurrentUser();
    const showReferrals = useShowReferrals();
    const isSettings = isSettingsPath(
        currentUser,
        pathname,
        showReferrals.enabled
    );

    const sidebarNavItems = isSettings
        ? buildSettingsNavItems({
              currentUser,
              pathname,
              showReferrals: showReferrals.enabled,
          })
        : buildMainNavItems({
              currentUser,
              pathname,
              exportActionText,
          });

    const { pageMode, setPageMode } = usePageContext();
    const showMine =
        pageMode === 'MINE' &&
        currentUser.activePartnershipId &&
        !isSingleplayer(currentUser);

    const isShutdownBannerVisible = useIsShutdownBannerVisible();

    return (
        <FlexCol
            justify="space-between"
            position="fixed"
            top={isShutdownBannerVisible ? SHUTDOWN_BANNER_HEIGHT : 0}
            bottom={0}
            transition="background 0.3s ease-in-out"
            bg={
                isSettings
                    ? 'gray.50'
                    : showMine
                      ? PAGE_MODE_MINE_COLOR
                      : 'white'
            }
            w={SIDEBAR_WIDTH}
            zIndex={1}
        >
            <FlexCol pt={9} px={11}>
                <NextLink href="/" passHref legacyBehavior>
                    <IconButton
                        aria-label="home icon"
                        icon={<PlentyLogo size={11} />}
                        variant="ghost"
                        colorScheme={
                            showMine && !isSettings ? 'blackAlpha' : 'gray'
                        }
                        color={showMine && !isSettings ? 'white' : 'textDark'}
                        w={11}
                        h={11}
                        ml={-1}
                        onClickEvent="Plenty Logo Button Clicked"
                    />
                </NextLink>
                <SidebarNav
                    mt={11}
                    mb={8}
                    items={sidebarNavItems}
                    isSettings={isSettings}
                    showMine={!!showMine}
                />
                {isSettings ? (
                    <Button
                        as={NextLink}
                        href="/"
                        variant="outline"
                        colorScheme="dark"
                        h={11}
                        w="100px"
                        {...hoverStyleProps({
                            bg: 'white',
                        })}
                        onClickEvent="Settings Back Button Clicked"
                    >
                        {'<-'}&nbsp;&nbsp;Back
                    </Button>
                ) : (
                    currentUser.activePartnershipId &&
                    !isSingleplayer(currentUser) && (
                        <PageModeToggle
                            value={pageMode}
                            onChanged={(v) => {
                                setPageMode(v);
                            }}
                        />
                    )
                )}
            </FlexCol>
            <FlexCol gap={4}>
                <FlexCol align="center" gap={4} px={4}>
                    <IDVStatusButton />
                    <PartnerInviteButton />
                    <ProductboardButton />
                    <PrivacyModeTag />
                </FlexCol>
                <FlexRow
                    as={NextLink}
                    href="/profile"
                    h={16}
                    align="center"
                    justify="space-between"
                    px={6}
                    gap={4}
                    {...hoverStyleProps({
                        bg: showMine ? 'whiteAlpha.100' : 'gray.100',
                    })}
                >
                    <FlexRow align="center" gap={4}>
                        <UserAvatar />

                        <Text
                            color="gray.400"
                            whiteSpace="nowrap"
                            overflow="hidden"
                            textOverflow="ellipsis"
                            maxW={100}
                        >
                            {getPreferredOrFirstName(currentUser.userInfo)}
                        </Text>
                    </FlexRow>
                    <Settings02 size="sm" color={'gray.400'} />
                </FlexRow>
            </FlexCol>
        </FlexCol>
    );
};

export default LeftNavSidebar;
